import React, { useEffect, useState } from "react";
import {
  Checkbox,
  DatePicker,
  DefaultButton,
  Dropdown,
  FontWeights,
  IButtonStyles,
  IconButton,
  IDatePickerStyles,
  IDropdownOption,
  IIconProps,
  IStackTokens,
  Label,
  mergeStyleSets,
  Modal,
  Stack,
  StackItem,
  TextField,
  useTheme,
} from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { IProductInfo } from "@models/dto/Rocketta/LicenseServer/Models/Products/IProductInfo";
import { IFeatureModel } from "@models/dto/Rocketta/LicenseServer/Models/IFeatureModel";
import { ProductApi } from "@api/productsApi";
import { extractErrorMessage, toErrorMessageBar, toSuccessMessageBar } from "@helpers/alertUtils";
import { IIssueLicenseRequest } from "@models/dto/Rocketta/LicenseServer/Models/Licenses/IIssueLicenseRequest";
import { formatDate } from "@helpers/converters";
import { ILicenseInfo } from "@models/dto/Rocketta/LicenseServer/Models/Licenses/ILicenseInfo";
import { LicensesApi } from "@api/licensesApi";

export interface INewLicenseModalProps {
  productId?: number;
  customerUrl?: string;
  newLicenseModalVisible: boolean;
  hideNewLicenseModal: () => void;
}
const cancelIcon: IIconProps = { iconName: "Cancel" };
const tokens: IStackTokens = { childrenGap: 10 };
export const NewLicenseModal: React.FC<INewLicenseModalProps> = (props: INewLicenseModalProps) => {
  const { productId, customerUrl, newLicenseModalVisible, hideNewLicenseModal } = props;

  const [products, setProducts] = useState<IProductInfo[]>([]);
  const [selectedProductInfo, setSelectedProductInfo] = useState<IProductInfo | undefined>();

  const [checkedFeatures, setCheckedFeatures] = useState<IFeatureModel[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const [issuedLicenseInfo, setIssuedLicenseInfo] = useState<ILicenseInfo | undefined>();

  const theme = useTheme();
  const titleId = useId("title");
  const [url, setUrl] = useState<string>(customerUrl);
  const [expirationDate, setExpirationDate] = useState<Date>(null);
  const iconButtonStyles: Partial<IButtonStyles> = {
    root: {
      color: theme.palette.neutralPrimary,
      marginLeft: "auto",
      marginTop: "4px",
      marginRight: "2px",
    },
    rootHovered: {
      color: theme.palette.neutralDark,
    },
  };

  const contentStyles = mergeStyleSets({
    container: {
      display: "flex",
      flexFlow: "column nowrap",
      alignItems: "stretch",
      width: "500px",
    },
    header: [
      theme.fonts.xLargePlus,
      {
        flex: "1 1 auto",
        borderTop: `4px solid ${theme.palette.themePrimary}`,
        color: theme.palette.neutralPrimary,
        display: "flex",
        alignItems: "center",
        fontWeight: FontWeights.semibold,
        padding: "12px 12px 14px 24px",
      },
    ],
    heading: {
      color: theme.palette.neutralPrimary,
      fontWeight: FontWeights.semibold,
      fontSize: "inherit",
      margin: "0",
    },
    body: {
      flex: "4 4 auto",
      padding: "0 24px 24px 24px",
      overflowY: "hidden",
      selectors: {
        p: { margin: "14px 0" },
        "p:first-child": { marginTop: 0 },
        "p:last-child": { marginBottom: 0 },
      },
    },
  });

  const datePickerStyles: Partial<IDatePickerStyles> = {
    root: {
      width: "200px",
    },
    textField: {
      width: "100%",
    },
  };

  const selectProductInfo = (id: number) => {
    ProductApi.getProduct(id)
      .then((result) => {
        setSelectedProductInfo(result.data.data);
        return result.data.data;
      })
      .catch((err) => {
        const message = extractErrorMessage(err);
        setErrorMessage(message);
      });
  };

  useEffect(() => {
    if (productId) {
      selectProductInfo(productId);
    } else {
      ProductApi.getProducts({})
        .then((result) => {
          setProducts(result.data.data);
          return result.data.data;
        })
        .catch((err) => {
          const message = extractErrorMessage(err);
          setErrorMessage(message);
        });
    }
  }, [productId]);

  const onProductSelect = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    selectProductInfo(Number(item.key));
  };

  const isChecked = (feature: IFeatureModel): boolean => {
    return checkedFeatures.some((f) => f.id === feature.id);
  };

  const onCheckedFeature = (id: number, checked: boolean) => {
    if (checked) {
      const feature = selectedProductInfo.features.find((f) => f.id == id);
      setCheckedFeatures([...checkedFeatures, feature]);
    } else {
      setCheckedFeatures(checkedFeatures.filter((f) => f.id !== id));
    }
  };

  const onSaveButtonClick = async (): Promise<void> => {
    const issueLicenseRequest: IIssueLicenseRequest = {
      url: url,
      productId: selectedProductInfo?.productId,
      features: checkedFeatures.map((f) => f.id),
    };
    if (expirationDate) issueLicenseRequest.expirationDate = formatDate(expirationDate);
    try {
      const licenseResponse = await LicensesApi.issueLicense(issueLicenseRequest);
      setIssuedLicenseInfo(licenseResponse.data.data);
    } catch (err) {
      const msg = extractErrorMessage(err);
      setErrorMessage(msg);
    }
  };

  return (
    <Modal
      titleAriaId={titleId}
      isOpen={newLicenseModalVisible}
      onDismiss={hideNewLicenseModal}
      isBlocking={true}
      containerClassName={contentStyles.container}
    >
      <div className={contentStyles.header}>
        <h2 className={contentStyles.heading} id={titleId}>
          Issue license
        </h2>
        <IconButton
          styles={iconButtonStyles}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={hideNewLicenseModal}
        />
      </div>
      <div className={contentStyles.body}>
        <Stack tokens={tokens}>
          <StackItem>
            {productId ? (
              <TextField disabled value={selectedProductInfo?.name} label="Product" />
            ) : (
              <Dropdown
                label="Product"
                options={products.map((p) => ({
                  key: p.id,
                  text: p.name,
                }))}
                onChange={onProductSelect}
              />
            )}
          </StackItem>
          <StackItem>
            <TextField
              disabled={customerUrl !== undefined}
              value={url}
              onChange={(ev, newValue) => setUrl(newValue)}
              label="Url"
            />
          </StackItem>
          <StackItem>
            <DatePicker
              styles={datePickerStyles}
              value={expirationDate}
              onSelectDate={(newValue) => setExpirationDate(newValue)}
              label="Expiration date"
            />
          </StackItem>

          {selectedProductInfo && selectedProductInfo.features?.length > 0 && (
            <>
              <StackItem>
                <Label>Features</Label>
              </StackItem>
              <StackItem>
                {selectedProductInfo.features.map((f) => (
                  <Checkbox
                    label={f.name}
                    key={f.id}
                    onChange={(ev, checked) => onCheckedFeature(f.id, checked)}
                    checked={isChecked(f)}
                    styles={{ root: { marginTop: 5 } }}
                  />
                ))}
              </StackItem>
            </>
          )}
          <StackItem>
            <DefaultButton onClick={onSaveButtonClick}>Save</DefaultButton>
          </StackItem>
          {errorMessage && <StackItem>{toErrorMessageBar(errorMessage)}</StackItem>}
          {issuedLicenseInfo && (
            <StackItem>{toSuccessMessageBar(`Success! LicenseId: ${issuedLicenseInfo.licenseId}`)}</StackItem>
          )}
        </Stack>
      </div>
    </Modal>
  );
};
